import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { ParkingCircle, Siren, ShieldAlert, Check, X, CheckCircle2, Clock, MapPin, Phone, RefreshCw, BellRing, Ban, Lightbulb, DoorOpen, AlertTriangle, MessageSquare } from "lucide-react";

const META = {
  wrong_parking: { icon: <ParkingCircle size={18} />, color: "#f5a524", label: "Wrong parking" },
  vehicle_blocking: { icon: <Ban size={18} />, color: "#fb923c", label: "Vehicle blocking" },
  headlight_on: { icon: <Lightbulb size={18} />, color: "#eab308", label: "Headlight ON" },
  door_open: { icon: <DoorOpen size={18} />, color: "#e92546", label: "Door / window open" },
  emergency: { icon: <Siren size={18} />, color: "#ff3b5c", label: "Emergency" },
  vehicle_damage: { icon: <AlertTriangle size={18} />, color: "#f97316", label: "Vehicle damage" },
  other: { icon: <MessageSquare size={18} />, color: "#ff4d67", label: "Other" },
  accident: { icon: <Siren size={18} />, color: "#ff3b5c", label: "Accident" },
  theft: { icon: <ShieldAlert size={18} />, color: "#ff4d67", label: "Theft / suspicious" },
};
const statusLabel = (s) => ({ alert_sent: "Alert sent", coming: "You're coming", no_response: "No response", call_attempted: "Call attempted", resolved: "Resolved" }[s] || s);

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const itemsRef = useRef([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/incidents")).data.results); } finally { setLoading(false); }
  }, []);

  // Live-sync across family members: poll the lightweight status feed and merge
  // the changed fields in place (no photo re-download). If a brand-new incident
  // appears, pull the full list once to get its photos/details.
  const syncStatuses = useCallback(async () => {
    let live;
    try { live = (await api.get("/incidents/status")).data.results || []; }
    catch (_) { return; }
    const prevIds = new Set(itemsRef.current.map((x) => x.id));
    if (live.some((x) => !prevIds.has(x.id))) { load(); return; }
    const map = Object.fromEntries(live.map((x) => [x.id, x]));
    setItems((cur) => cur.map((it) => (map[it.id] ? { ...it, ...map[it.id] } : it)));
  }, [load]);

  useEffect(() => {
    load();
    const t = setInterval(syncStatuses, 8000);
    const onVis = () => { if (document.visibilityState === "visible") syncStatuses(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [load, syncStatuses]);

  const respond = async (id, response) => { await api.post(`/incidents/${id}/respond`, { response }); syncStatuses(); };
  const resolve = async (id) => { await api.post(`/incidents/${id}/resolve`); syncStatuses(); };
  const extend = async (id) => { await api.post(`/incidents/${id}/extend`); syncStatuses(); };

  return (
    <div className="page" data-testid="incidents-page">
      <div className="container-nk" style={{ maxWidth: 760 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div><span className="chip"><BellRing size={13} /> Incidents</span><h1 style={{ fontSize: 34, marginTop: 12 }}>QR scan <span className="neon">incidents</span></h1></div>
          <button className="btn btn-ghost btn-sm" onClick={load} data-testid="refresh-incidents"><RefreshCw size={15} /> Refresh</button>
        </div>

        {loading ? <div className="spinner" /> : items.length === 0 ? (
          <div className="glass card-pad center" style={{ padding: 50 }} data-testid="empty-incidents">
            <BellRing size={40} color="#b3122f" /><h3 style={{ fontSize: 22, margin: "12px 0 6px" }}>No incidents yet</h3>
            <p className="muted">When someone scans your QR and reports an issue, respond here.</p>
          </div>
        ) : (
          <>
            <div className="glass card-pad" style={{ padding: 16, marginBottom: 16 }} data-testid="reason-breakdown">
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><BellRing size={15} color="#b3122f" /> Alert reasons breakdown</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(items.reduce((a, it) => { a[it.type] = (a[it.type] || 0) + 1; return a; }, {})).sort((x, y) => y[1] - x[1]).map(([t, c]) => {
                  const m = META[t] || META.wrong_parking;
                  return (
                    <span key={t} className="chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${m.color}18`, borderColor: `${m.color}55`, color: "#fff" }} data-testid={`reason-stat-${t}`}>
                      <span style={{ color: m.color, display: "inline-flex" }}>{m.icon}</span> {m.label} · <b>{c}</b>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="grid" style={{ gap: 14 }}>
            {items.map((it) => {
              const m = META[it.type] || META.wrong_parking;
              const pending = !it.resolved && (it.status === "alert_sent" || it.status === "no_response");
              return (
                <div key={it.id} className="glass card-pad" style={{ padding: 20 }} data-testid={`incident-${it.id}`}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div className="alert-ico" style={{ background: `${m.color}22`, color: m.color }}>{m.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                        <span style={{ fontWeight: 700 }}>{m.label} · {it.number_plate}</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span className="chip" style={{ padding: "2px 10px", fontSize: 11, background: it.resolved ? "rgba(233,37,70,.16)" : `${m.color}22`, borderColor: it.resolved ? "rgba(233,37,70,.4)" : `${m.color}55`, color: it.resolved ? "#e92546" : "#fff" }}>{statusLabel(it.status)}</span>
                          {/* Location icon on EVERY incident: opens the scan spot in Maps when the
                              reporter's GPS was captured; greyed out when it wasn't. */}
                          {it.scanner_lat != null ? (
                            <a href={`https://maps.google.com/?q=${it.scanner_lat},${it.scanner_lng}`} target="_blank" rel="noreferrer" title="Scan location on map" aria-label="Scan location on map" data-testid={`incident-map-${it.id}`}
                               style={{ width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#b3122f,#e92546)", color: "#fff", flexShrink: 0, boxShadow: "0 4px 14px rgba(233,37,70,.35)" }}>
                              <MapPin size={18} />
                            </a>
                          ) : (
                            <button type="button" title="Location was not captured for this scan" aria-label="Location not captured" data-testid={`incident-map-none-${it.id}`}
                                    onClick={() => alert("Location was not captured for this scan — the reporter's phone had no GPS fix at the time.")}
                                    style={{ width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#b3122f,#e92546)", color: "#fff", flexShrink: 0, border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(233,37,70,.35)" }}>
                              <MapPin size={18} />
                            </button>
                          )}
                        </span>
                      </div>
                      {it.scanner_note && <p className="muted" style={{ fontSize: 14, marginTop: 6 }}>{it.scanner_note}</p>}
                      {(it.evidence_photo_base64 || it.reporter_photo_base64) && (
                        <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }} data-testid={`incident-photos-${it.id}`}>
                          {it.evidence_photo_base64 && (
                            <a href={it.evidence_photo_base64} target="_blank" rel="noreferrer" title="Vehicle photo" data-testid={`incident-evidence-${it.id}`}>
                              <img src={it.evidence_photo_base64} alt="Vehicle" style={{ width: 92, height: 92, objectFit: "cover", borderRadius: 12, border: "1px solid rgba(233,37,70,.4)" }} />
                              <span className="muted" style={{ display: "block", fontSize: 11, marginTop: 3 }}>Vehicle</span>
                            </a>
                          )}
                          {it.reporter_photo_base64 && (
                            <a href={it.reporter_photo_base64} target="_blank" rel="noreferrer" title="Reporter photo" data-testid={`incident-reporter-${it.id}`}>
                              <img src={it.reporter_photo_base64} alt="Reporter" style={{ width: 92, height: 92, objectFit: "cover", borderRadius: 12, border: "1px solid rgba(245,165,36,.5)" }} />
                              <span className="muted" style={{ display: "block", fontSize: 11, marginTop: 3 }}>Reporter</span>
                            </a>
                          )}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap", fontSize: 13 }}>
                        {(it.type === "wrong_parking" || it.type === "vehicle_blocking") && !it.resolved && <span className="muted"><Clock size={13} style={{ verticalAlign: "-2px" }} /> {it.minutes_left} min left</span>}
                        {it.scanner_phone && <a href={`tel:${it.scanner_phone}`} className="neon" style={{ fontWeight: 700 }}><Phone size={12} style={{ verticalAlign: "-1px" }} /> Reporter: {it.scanner_phone}</a>}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                        {pending && (
                          <>
                            <button className="btn btn-primary btn-sm" onClick={() => respond(it.id, "coming")} data-testid={`coming-${it.id}`}><Check size={15} /> I AM COMING</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => respond(it.id, "cant")} data-testid={`cant-${it.id}`}><X size={15} /> Can't respond</button>
                          </>
                        )}
                        {!it.resolved && (it.type === "wrong_parking" || it.type === "vehicle_blocking") && (
                          <button className="btn btn-ghost btn-sm" onClick={() => extend(it.id)} data-testid={`extend-${it.id}`}><Clock size={15} color="#f5a524" /> Need more time (+10 min)</button>
                        )}
                        {!it.resolved && <button className="btn btn-ghost btn-sm" onClick={() => resolve(it.id)} data-testid={`resolve-${it.id}`}><CheckCircle2 size={15} color="#e92546" /> Mark resolved</button>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
